import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (lineItemRow) {
      const packId = this.#getPackIdFromRow(lineItemRow);

      // If this is a parent pack row, sync all child rows to the same quantity.
      if (packId && lineItemRow.dataset.pack) {
        const packRows = this.#getRowsByPackId(packId);

        const parentKey = lineItemRow.dataset.key;
        const childKeys = packRows
          .filter((row) => row !== lineItemRow && row.dataset.linkedPack === packId)
          .map((row) => row.dataset.key)
          .filter(Boolean);

        // If there are children, update the whole pack and skip the single-line flow.
        if (childKeys.length) {
          this.#updatePackQuantitiesByKey({
            parentKey,
            childKeys,
            quantity,
            action: 'change',
            fallbackLine: line,
          });
          return;
        }
      }

      const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
      textComponent?.shimmer();
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
  }

  /**
   * Gets the Pack ID from a cart row.
   * Parent rows use `data-pack`; child rows use `data-linked-pack`.
   * @param {HTMLTableRowElement} row
   * @returns {string | null}
   */
  #getPackIdFromRow(row) {
    const v = row.dataset.pack || row.dataset.linkedPack;
    return v ? String(v).trim() || null : null;
  }

  /**
   * Returns all cart rows that belong to the given Pack ID.
   * @param {string} packId
   * @returns {HTMLTableRowElement[]}
   */
  #getRowsByPackId(packId) {
    if (!packId) return [];

    return this.refs.cartItemRows.filter((row) => {
      const v = this.#getPackIdFromRow(row);
      return v && v === packId;
    });
  }

  /**
   * Updates a parent pack line and all its child lines to the same quantity.
   * Performance: only request rendered sections for the LAST change call.
   * @param {Object} config
   * @param {string} config.parentKey
   * @param {string[]} config.childKeys
   * @param {number} config.quantity
   * @param {string} config.action
   * @param {number} config.fallbackLine
   */
  async #updatePackQuantitiesByKey(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    cartTotal?.shimmer();

    const ids = [config.parentKey, ...config.childKeys].filter(Boolean);

    try {
      /** @type {any} */
      let lastParsed = null;

      for (let i = 0; i < ids.length; i++) {
        const id = ids[i];
        const isLast = i === ids.length - 1;

        const payload = isLast
          ? {
              id,
              quantity: config.quantity,
              sections: Array.from(sectionsToUpdate).join(','),
              sections_url: window.location.pathname,
            }
          : { id, quantity: config.quantity };

        const body = JSON.stringify(payload);

        const resp = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
        const txt = await resp.text();
        const parsed = JSON.parse(txt);

        if (parsed?.errors) {
          this.#handleCartError(config.fallbackLine, parsed);
          resetShimmer(this);
          return false;
        }

        if (isLast) lastParsed = parsed;
      }

      resetShimmer(this);

      if (lastParsed?.sections?.[this.sectionId]) {
        morphSection(this.sectionId, lastParsed.sections[this.sectionId]);

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(lastParsed);

        this.dispatchEvent(
          new CartUpdateEvent(lastParsed, this.sectionId, {
            itemCount: Number(lastParsed.item_count ?? 0),
            source: 'cart-items-component',
            sections: lastParsed.sections,
          })
        );

        this.#updateCartQuantitySelectorButtonStates();
      }

      return true;
    } catch (error) {
      console.error(error);
      resetShimmer(this);
      return false;
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Handles the line item removal.
   * Removes the clicked parent line AND all related child lines that share the same Pack ID.
   * Uses the stable line item key via /cart/change.js (`id`) to avoid line shifting issues.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const packId = this.#getPackIdFromRow(cartItemRowToRemove);
    const packageRows = packId ? this.#getRowsByPackId(packId) : [];

    const nestedRows = this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key);

    const rowsToRemove = Array.from(new Set([cartItemRowToRemove, ...nestedRows, ...packageRows]));

    // 1) Server-side removal: clear by stable line item key, sequentially (prevents request races).
    const keysToClear = rowsToRemove.map((row) => row.dataset.key).filter(Boolean);
    const uniqueKeysToClear = Array.from(new Set(keysToClear));

    if (uniqueKeysToClear.length) {
      const didClear = await this.#clearLineItemKeys(uniqueKeysToClear, line);

      if (didClear) {
        const cartItemsHtml = didClear.sections?.[this.sectionId];

        if (cartItemsHtml) {
          morphSection(this.sectionId, cartItemsHtml);

          // Update data-cart-quantity for all matching variants
          this.#updateQuantitySelectors(didClear);

          // Update button states after morph
          this.#updateCartQuantitySelectorButtonStates();

          // Emit a CartUpdateEvent so other listeners (e.g. cart drawer/header) can sync
          this.dispatchEvent(
            new CartUpdateEvent(didClear, this.sectionId, {
              itemCount: Number(didClear.item_count ?? 0),
              source: 'cart-items-component',
              sections: didClear.sections,
            })
          );

          return;
        }

        // Fallback if sections weren't returned
        await sectionRenderer.renderSection(this.sectionId, { cache: false });
        this.#updateCartQuantitySelectorButtonStates();
        return;
      }
    }

    // Fallback: animate-remove rows only when we couldn't confirm a successful server update.
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      onAnimationEnd(row, remove);
    });
  }

  /**
   * Clears multiple cart lines by stable line item keys (id) sequentially.
   * Performance: only request rendered sections for the LAST change call.
   * @param {string[]} keys
   * @param {number} fallbackLine
   * @returns {Promise<any | false>} Returns the last parsed cart response (with sections) or false on error.
   */
  async #clearLineItemKeys(keys, fallbackLine) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`clear:user-action`);

    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    cartTotal?.shimmer();

    try {
      /** @type {any} */
      let lastParsed = null;

      for (let i = 0; i < keys.length; i++) {
        const id = keys[i];
        const isLast = i === keys.length - 1;

        const payload = isLast
          ? {
              id,
              quantity: 0,
              sections: Array.from(sectionsToUpdate).join(','),
              sections_url: window.location.pathname,
            }
          : { id, quantity: 0 };

        const body = JSON.stringify(payload);

        const resp = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
        const txt = await resp.text();
        const parsed = JSON.parse(txt);

        if (parsed?.errors) {
          this.#handleCartError(fallbackLine, parsed);
          resetShimmer(this);
          return false;
        }

        if (isLast) lastParsed = parsed;
      }

      resetShimmer(this);
      return lastParsed || true;
    } catch (error) {
      console.error(error);
      resetShimmer(this);
      return false;
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates the cart using the stable line item key (id) via /cart/change.js.
   * @param {Object} config - The config.
   * @param {string} config.id - The line item key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {number} [config.fallbackLine] - Used to render errors on an existing line.
   */
  updateQuantityByKey(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { id, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          const fallbackLine = Number(config.fallbackLine || 0);
          if (fallbackLine) this.#handleCartError(fallbackLine, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
